import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import flatpickr from "flatpickr";
import { Boxes, Search, Trash2, User, Users } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../shared/ui/icons/SubjectKindIcon";
import { Modal } from "../../../../shared/ui/modal";
import { useTherapist } from "../../../../context/useTherapist";
import { offeredCaseload, useCourseLabels, useSessionPreferences } from "../../../center";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import DatePicker from "../../../../shared/ui/form/date-picker";
import Label from "../../../../shared/ui/form/Label";
import { loadConstellationCaseload } from "../../application/constellationCaseload";
import { deleteConstellationSession, listConstellationSessions } from "../../application/constellationSessions";
import type { ConstellationSessionItem, SessionType } from "../../domain/types";
import { groupLabel, type GroupPatient } from "../../../patient";
import type { Patient, CouplePatient } from "../../../patient";

const PAGE_SIZE = 8;
const TABS: Array<{ key: "all" | SessionType; labelKey: string }> = [
    { key: "all", labelKey: "constellation.sessionsList.all" },
    { key: "individual", labelKey: "common.individual" },
    { key: "couple", labelKey: "common.couple" },
    { key: "group", labelKey: "common.group" },
    { key: "anonymous", labelKey: "constellation.sessionsList.anonymous" },
];

const TYPE_BADGE: Record<SessionType, string> = {
    individual: "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300",
    couple: "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    group: "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    anonymous: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

type Props = { isOpen: boolean; onClose: () => void };

export default function ConstellationSessionsModal({ isOpen, onClose }: Props) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { therapist } = useTherapist();
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();
    // Couples and groups only where the centre runs them.
    const { modes } = useSessionPreferences();

    const [tab, setTab] = useState<"all" | SessionType>("all");
    const [fromDate, setFromDate] = useState(""); // "Y-m-d"
    const [toDate, setToDate] = useState("");
    const [subject, setSubject] = useState<string>(""); // "patient:<id>" | "couple:<id>" | "group:<id>"
    const [offset, setOffset] = useState(0);

    const [sessions, setSessions] = useState<ConstellationSessionItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [subjects, setSubjects] = useState<{ value: string; label: string; icon: React.ReactNode }[]>([]);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!isOpen || !therapist?.id) return;
        loadConstellationCaseload(therapist.id)
            .then((caseload) => offeredCaseload(caseload, modes))
            .then(({ patients, couples, groups }) => {
                setSubjects([
                    ...(patients || []).map((p: Patient) => ({
                        value: `patient:${p.id}`,
                        label: labelOf(p),
                        icon: <User className="h-4 w-4" />,
                    })),
                    ...(couples || []).map((c: CouplePatient) => ({
                        value: `couple:${c.id}`,
                        label: c.display_name,
                        icon: <Users className="h-4 w-4" />,
                    })),
                    ...(groups || []).map((g: GroupPatient) => ({
                        value: `group:${g.id}`,
                        label: groupLabel(g),
                        icon: <GroupOfPeopleIcon className="h-4 w-4" />,
                    })),
                ]);
            })
            .catch(() => setSubjects([]));
    }, [isOpen, therapist?.id, labelOf, modes]);

    // Reset to first page whenever a filter changes.
    useEffect(() => {
        setOffset(0);
    }, [tab, fromDate, toDate, subject]);

    useEffect(() => {
        if (!isOpen || !therapist?.id) return;
        setLoading(true);
        const [kind, id] = subject.split(":");
        listConstellationSessions({
            session_type: tab === "all" ? undefined : tab,
            from_date: fromDate ? new Date(`${fromDate}T00:00:00`).toISOString() : undefined,
            to_date: toDate ? new Date(`${toDate}T23:59:59`).toISOString() : undefined,
            patient_id: kind === "patient" ? id : undefined,
            couple_patient_id: kind === "couple" ? id : undefined,
            group_patient_id: kind === "group" ? id : undefined,
            offset,
            limit: PAGE_SIZE,
        })
            .then((data) => {
                setSessions(data.sessions);
                setTotal(data.total);
            })
            .catch(() => {
                setSessions([]);
                setTotal(0);
            })
            .finally(() => setLoading(false));
    }, [isOpen, therapist?.id, tab, fromDate, toDate, subject, offset, refreshKey]);

    const fmtDate = useMemo(
        () =>
            new Intl.DateTimeFormat(i18n.language, {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }),
        [i18n.language],
    );

    const clearFilters = () => {
        setTab("all");
        setFromDate("");
        setToDate("");
        setSubject("");
    };

    const hasFilters = tab !== "all" || fromDate || toDate || subject;
    const page = Math.floor(offset / PAGE_SIZE) + 1;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const dateKey = `${fromDate}-${toDate}`;

    const openSession = (sessionId: string) => {
        onClose();
        navigate(`/session/${sessionId}`);
    };

    const deleteSession = async (s: ConstellationSessionItem) => {
        if (deletingId) return;
        const label =
            s.session_type === "anonymous"
                ? t("constellation.sessionsList.anonymous")
                : s.subject_name || t("constellation.sessionsList.untitled");
        if (!window.confirm(t("constellation.sessionsList.deleteConfirm", { name: label }))) return;
        setDeletingId(s.session_id);
        try {
            await deleteConstellationSession(s.session_id);
            setRefreshKey((k) => k + 1); // refetch the current page
        } catch {
            // ignore; the row stays and the therapist can retry
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="mx-4 max-w-3xl rounded-3xl">
            <div className="flex max-h-[85vh] flex-col p-5 sm:p-6">
                <div className="mb-4 flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-900/30">
                        <Boxes className="h-5 w-5" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-content-primary">
                            {t("constellation.sessionsList.title")}
                        </h2>
                        <p className="text-xs text-content-tertiary">
                            {t("constellation.sessionsList.subtitle")}
                        </p>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-4 flex flex-col gap-3">
                    <div className="flex flex-wrap gap-1.5">
                        {TABS.map((tb) => (
                            <button
                                key={tb.key}
                                type="button"
                                onClick={() => setTab(tb.key)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                    tab === tb.key
                                        ? "bg-brand-600 text-white"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
                                }`}
                            >
                                {t(tb.labelKey)}
                            </button>
                        ))}
                    </div>
                    <FilterSelect
                        icon={<Search className="h-4 w-4" />}
                        options={subjects}
                        value={subject}
                        onChange={(v) => setSubject(typeof v === "string" ? v : "")}
                        placeholder={t("constellation.sessionsList.subjectFilter")}
                        searchPlaceholder={t("patient.searchPlaceholder", "Search...")}
                        className="w-full bg-gray-50 dark:bg-gray-800/50"
                    />
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div>
                            <Label htmlFor="cs-from-date">{t("constellation.sessionsList.fromDate")}</Label>
                            <DatePicker
                                key={`from-${dateKey}`}
                                id="cs-from-date"
                                label=""
                                placeholder={t("session.selectStartDate")}
                                defaultDate={fromDate}
                                onChange={(dates: Date[]) =>
                                    setFromDate(dates[0] ? flatpickr.formatDate(dates[0], "Y-m-d") : "")
                                }
                            />
                        </div>
                        <div>
                            <Label htmlFor="cs-to-date">{t("constellation.sessionsList.toDate")}</Label>
                            <DatePicker
                                key={`to-${dateKey}`}
                                id="cs-to-date"
                                label=""
                                placeholder={t("session.selectEndDate")}
                                defaultDate={toDate}
                                onChange={(dates: Date[]) =>
                                    setToDate(dates[0] ? flatpickr.formatDate(dates[0], "Y-m-d") : "")
                                }
                            />
                        </div>
                    </div>
                    {hasFilters && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            className="self-start text-xs font-medium text-brand-600 hover:underline"
                        >
                            {t("common.clear", "Clear")}
                        </button>
                    )}
                </div>

                {/* List */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="space-y-2">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" />
                            ))}
                        </div>
                    ) : sessions.length === 0 ? (
                        <p className="py-10 text-center text-sm text-content-tertiary">
                            {t("constellation.sessionsList.empty")}
                        </p>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                            {sessions.map((s) => (
                                <li key={s.board_id} className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => openSession(s.session_id)}
                                        className="flex min-w-0 flex-1 items-center gap-3 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40"
                                    >
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800">
                                            {s.session_type === "group" ? (
                                                <GroupOfPeopleIcon className="h-5 w-5" />
                                            ) : s.session_type === "couple" ? (
                                                <Users className="h-5 w-5" />
                                            ) : s.session_type === "anonymous" ? (
                                                <Boxes className="h-5 w-5" />
                                            ) : (
                                                <User className="h-5 w-5" />
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">
                                                {s.title || t("constellation.sessionsList.untitled")}
                                            </p>
                                            <p className="truncate text-xs text-gray-500">
                                                {s.session_type === "anonymous"
                                                    ? t("constellation.sessionsList.anonymous")
                                                    : s.subject_name || "—"}
                                            </p>
                                        </div>
                                        <span
                                            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${TYPE_BADGE[s.session_type]}`}
                                        >
                                            {t(
                                                s.session_type === "anonymous"
                                                    ? "constellation.sessionsList.anonymous"
                                                    : `common.${s.session_type}`,
                                            )}
                                        </span>
                                        <span className="hidden shrink-0 text-xs text-content-tertiary sm:inline">
                                            {fmtDate.format(new Date(s.date))}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => deleteSession(s)}
                                        disabled={deletingId === s.session_id}
                                        aria-label={t("constellation.sessionsList.delete")}
                                        title={t("constellation.sessionsList.delete")}
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-content-tertiary transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40 dark:hover:bg-red-900/20"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Pagination */}
                {total > PAGE_SIZE && (
                    <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                        <span>{t("constellation.sessionsList.pageOf", { page, pageCount })}</span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={offset === 0}
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                className="rounded-lg border border-gray-200 px-3 py-1 font-medium disabled:opacity-40 dark:border-gray-700"
                            >
                                {t("common.previous", "Previous")}
                            </button>
                            <button
                                type="button"
                                disabled={offset + PAGE_SIZE >= total}
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                                className="rounded-lg border border-gray-200 px-3 py-1 font-medium disabled:opacity-40 dark:border-gray-700"
                            >
                                {t("common.next", "Next")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
